/**
 * The interpolation panel: two endpoints, a step count, and what came back (`V143`-`V146`, P30.10).
 *
 * **A renderer with no state of its own beyond the DOM.** `panels/trajectory.ts` is the pattern:
 * the pane owns the endpoints and the result, hands this a `View`, and this turns it into controls
 * and calls back.
 *
 * ## Two controls, and why the run is a button rather than a click
 *
 * The mode is how the two endpoints get picked off the map; the button is how a run is started
 * once they are. That is what the reader asked for and it is also what the shape requires: a walk
 * needs *both* ends before it can be computed, so no single click can be the trigger.
 *
 * ## What the panel has to say that the screen cannot
 *
 * Three facts, each of which is measured and none of which is visible:
 *
 * | | measured | where it is said |
 * |---|---|---|
 * | the walk is computed in latent space, and what that draws depends on the projection kind | on PCA and raw views the steps land on a straight, evenly spaced line by construction; on UMAP the same latents draw a curve **1.449x** the chord with step spacing varying by **6.9x** | the help line, by projection kind |
 * | more steps stop finding more peptides | a pair yielding 13 unique sequences yields the same 13 at 50 steps and at 100; at 20 it already finds 11 | beside the step control, past `SATURATION_STEPS` |
 * | a "unique" step may be an endpoint's own reconstruction | over five random row pairs the first interior step matched `t = 0` in **five cases out of five** | the result summary, as its own count |
 *
 * ## The eviction notice
 *
 * `decoded.ts` evicts oldest-first and silently, which `V123` accepted for a point placed by one
 * click. A 50-step run can evict 50 in one gesture, which that reasoning does not cover — so this
 * panel says so before the request rather than after. It does not refuse: refusing would be
 * `encoded.ts`'s policy applied to a store whose whole point is that a click always works.
 */
import { MAX_STEPS, SATURATION_STEPS, type InterpolationStep, type Sampler } from '../api';
import type { EvictionForecast } from '../interpolation';
import { spanOf } from '../interpolation';
import { atLabel, originOf, styleOf, type TrajectoryPoint } from '../trajectory';
import { residueSpans } from './residues';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) node.append(child);
  return node;
}

/** One endpoint slot, and why it cannot be used if it cannot. */
export interface Endpoint {
  readonly point: TrajectoryPoint;
  /**
   * `null` when this endpoint is usable; otherwise the sentence saying why not.
   *
   * Two causes, and they are `main.ts`'s to detect: the point's coordinates belong to another
   * projection (`V115`, the same fact `statusOf` calls `off-projection`), or it is a pre-Phase-30
   * interpolated record that carries no recipe and so cannot be named to the server.
   */
  readonly refusal: string | null;
}

export interface InterpolateHandlers {
  onSteps(steps: number): void;
  onSwap(): void;
  onClearEndpoint(end: 'start' | 'end'): void;
  /** Empty both slots in one gesture. The per-slot `×` stays; this is the pair of them. */
  onClearEndpoints(): void;
  onRun(): void;
  onCancel(): void;
  /** Show one placed step in the detail pane. */
  onSelect(markId: string): void;
  /** Forget this run: its points leave the map, the store and any walk holding them. */
  onClearRun(): void;
  /** Whether an interior step that only reproduces an endpoint is placed too. */
  onIncludeEndpointMatches(include: boolean): void;
}

/** What one placed step looks like to the panel. */
export interface PlacedStep {
  readonly step: InterpolationStep;
  /** The `DecodedMark.id` of the point on the map, or `null` if it was not placed. */
  readonly markId: string | null;
}

export interface InterpolateView {
  readonly start: Endpoint | null;
  readonly end: Endpoint | null;
  readonly steps: number;
  readonly sampler: Sampler;
  /** `'pca' | 'umap' | 'raw'` — decides the help line, because the drawn path differs by kind. */
  readonly projectionKind: string;
  readonly includeEndpointMatches: boolean;
  /** What a run would evict from this view, computed before the request (§5.1). */
  readonly forecast: EvictionForecast;
  /** Set while a request is in flight. `loadingProjection` is the ~15 s first UMAP call. */
  readonly running: { loadingProjection: boolean } | null;
  /** The walk that came back, whole — every step, including the endpoints and the duplicates. */
  readonly result: readonly InterpolationStep[] | null;
  /** Aligned with the *placed* subset of `result`. */
  readonly placed: readonly PlacedStep[];
  /** Whether this pane is in interpolate mode, which decides the empty state. */
  readonly active: boolean;
}

export interface InterpolatePanel {
  show(view: InterpolateView, handlers: InterpolateHandlers): void;
  clear(): void;
}

/**
 * What the drawn walk means on each projection kind, in the one place that says it.
 *
 * **The PCA line is not a criticism of the feature, it is what the feature is for there**: the
 * coordinates carry nothing the two endpoints did not, and the *sequences* are the output. On UMAP
 * the coordinates carry something too, which is why the same run reads differently.
 */
const KIND_HELP: Record<string, string> = {
  pca: 'on a PCA view the transform is affine, so the steps land on a straight, evenly spaced line — the sequences are what this run tells you, not the positions',
  raw: 'on a raw-dimension view the steps land on a straight, evenly spaced line — the sequences are what this run tells you, not the positions',
  umap: 'on a UMAP view the same latents draw a curve about 1.45× the straight-line distance, with steps 7× further apart in places — which is why this walks the latent space and not the picture',
};

export function createInterpolatePanel(
  root: HTMLElement,
  /** Appended to every widget id, so pane B's controls do not collide with pane A's (P12.1). */
  suffix = '',
): InterpolatePanel {
  const id = (name: string) => `${name}${suffix}`;

  function slot(
    which: 'start' | 'end',
    endpoint: Endpoint | null,
    handlers: InterpolateHandlers,
  ): HTMLElement {
    const label = which === 'start' ? 'from' : 'to';
    if (endpoint === null) {
      return el(
        'li',
        { class: `endpoint empty ${which}` },
        el('span', { class: 'label' }, label),
        el('span', { class: 'facts' }, 'click a point on the map'),
      );
    }
    const clear = el(
      'button',
      { type: 'button', class: 'remove', title: `clear the ${label} endpoint` },
      '×',
    );
    clear.addEventListener('click', () => handlers.onClearEndpoint(which));
    const facts = [originOf(endpoint.point)];
    if (endpoint.refusal !== null) facts.push(endpoint.refusal);
    const node = el(
      'li',
      { class: `endpoint ${which} ${styleOf(endpoint.point)}` },
      el(
        'p',
        { class: 'head' },
        el('span', { class: 'label' }, label),
        el('span', { class: 'facts' }, facts.join(' · ')),
        clear,
      ),
    );
    if (endpoint.refusal !== null) node.classList.add('unusable');
    return node;
  }

  /** The step control, with the saturation note beside it rather than in a tooltip. */
  function stepControl(view: InterpolateView, handlers: InterpolateHandlers): HTMLElement {
    const input = el('input', {
      type: 'number',
      id: id('interpolate-steps'),
      min: '1',
      max: String(MAX_STEPS),
      step: '1',
      value: String(view.steps),
    });
    input.addEventListener('change', () => {
      const asked = Number.parseInt(input.value, 10);
      if (Number.isFinite(asked)) handlers.onSteps(Math.min(MAX_STEPS, Math.max(1, asked)));
    });
    const row = el('p', { class: 'label' }, el('label', {}, 'steps between them ', input));
    if (view.steps > SATURATION_STEPS) {
      row.append(
        el(
          'span',
          { class: 'warn' },
          ' extra steps may not be unique',
        ),
      );
    }
    return row;
  }

  /**
   * What to say when the walk produced nothing to place — **which is a real answer, not an empty
   * state**.
   *
   * Measured on `thanatin_seeded`, a 32-dimensional smoke checkpoint: every interior step of every
   * random row pair decoded to **one** sequence, and that sequence was the endpoints' own
   * reconstruction. So the run is complete, correct, and has nothing new in it — and a reader who
   * clicked `interpolate` and waited must be told that rather than shown an empty list.
   *
   * The two causes need different sentences, because the reader can act on one of them: everything
   * collapsed to sequences that are already on the map (the switch below places them), or the
   * decoder wrote nothing distinct along the line at all (a different pair, or a model with more to
   * say between these two points).
   */
  function nothingPlaced(
    result: readonly InterpolationStep[],
    included: boolean,
  ): HTMLElement | null {
    const interior = result.filter((s) => s.endpoint === null);
    const unique = interior.filter((s) => s.duplicate_of === null);
    const echoes = unique.filter((s) => s.matches_endpoint !== null);
    if (unique.length === 0) {
      return el(
        'p',
        { class: 'warn' },
        'the decoder wrote nothing along this line — every step between these two points came back empty',
      );
    }
    if (!included && echoes.length === unique.length) {
      return el(
        'p',
        { class: 'warn' },
        unique.length === 1
          ? 'every step decoded to one sequence, and it is the endpoints’ own reconstruction — so there is nothing between these two points that is not already on the map. Tick the box below to place it anyway.'
          : `all ${unique.length} distinct sequences on this line are the endpoints’ own reconstructions — nothing between these two points is new. Tick the box below to place them anyway.`,
      );
    }
    return null;
  }

  function summary(result: readonly InterpolationStep[]): HTMLElement {
    const interior = result.filter((s) => s.endpoint === null);
    const unique = interior.filter((s) => s.duplicate_of === null);
    const echoes = unique.filter((s) => s.matches_endpoint !== null);
    const parts = [
      `${interior.length} latents → ${unique.length} unique sequence${unique.length === 1 ? '' : 's'}`,
    ];
    // **Its own clause, never folded into the unique count** (`V145`): a step that reproduces an
    // endpoint is that endpoint's own reconstruction at a coordinate the endpoint does not sit at,
    // which is not a new peptide. Measured, this is not a rare case — five row pairs out of five.
    if (echoes.length > 0) {
      parts.push(
        `${echoes.length} of them the endpoints’ own reconstruction${echoes.length === 1 ? '' : 's'}`,
      );
    }
    return el('p', { class: 'label' }, parts.join(' · '));
  }

  function stepEntry(
    handlers: InterpolateHandlers,
    placed: PlacedStep,
    result: readonly InterpolationStep[],
    /**
     * This step's position among the ones that were **placed**, 1-based.
     *
     * **Not `step.index`, which is its position in the whole walk**, and the difference is not
     * cosmetic: the card on the map says `step 3 of 7`, counting the same way, and two surfaces
     * naming one point with two different numbers is exactly what `V136` forbids. The walk
     * position is not lost — `t` says where on the line it sits, to three decimals, and says it
     * more precisely than an ordinal out of twenty could.
     */
    at: number,
  ): HTMLElement {
    const step = placed.step;
    const [lo, hi] = spanOf(result, step);
    const ordinal = el(
      'button',
      { type: 'button', class: 'ordinal', title: 'show this point in the detail pane' },
      String(at),
    );
    if (placed.markId === null) ordinal.disabled = true;
    else ordinal.addEventListener('click', () => handlers.onSelect(placed.markId as string));

    const facts = [lo === hi ? `t = ${lo.toFixed(3)}` : `t = ${lo.toFixed(3)}–${hi.toFixed(3)}`];
    facts.push(`‖z‖ ${step.z_norm.toFixed(2)}`);
    facts.push(atLabel(step.xy));
    if (step.matches_endpoint !== null) {
      facts.push(`same as the ${step.matches_endpoint} point’s reconstruction`);
    }
    const node = el(
      'li',
      { class: 'step generated' },
      el('p', { class: 'head' }, ordinal, el('span', { class: 'facts' }, facts.join(' · '))),
      residueSpans(step.sequence, 'sequence generated'),
    );
    if (step.matches_endpoint !== null) node.classList.add('echo');
    return node;
  }

  function show(view: InterpolateView, handlers: InterpolateHandlers): void {
    // **Absent, not empty**, as the trajectory panel has it: with no endpoints, no result and the
    // mode elsewhere, there is nothing to say.
    if (view.start === null && view.end === null && view.result === null && !view.active) {
      root.replaceChildren();
      return;
    }

    const section = el(
      'section',
      { class: 'card interpolate', id: id('interpolate-card') },
      el('h2', {}, 'interpolate'),
    );

    const help = KIND_HELP[view.projectionKind];
    if (help !== undefined) section.append(el('p', { class: 'help' }, help));

    const slots = el('ul', { class: 'endpoints' });
    slots.append(slot('start', view.start, handlers), slot('end', view.end, handlers));
    section.append(slots);

    if (view.start !== null || view.end !== null) {
      const picked = el('p', { class: 'actions' });
      // **Only with both**, because there is nothing to exchange with an empty slot.
      if (view.start !== null && view.end !== null) {
        const swap = el(
          'button',
          { type: 'button', id: id('interpolate-swap'), title: 'walk the line the other way' },
          'swap ends',
        );
        swap.addEventListener('click', () => handlers.onSwap());
        picked.append(swap);
      }
      // **The pair of the two `×` controls, not a replacement for them** (`V148`): starting a new
      // line is one gesture rather than two, and clearing one slot to keep the other stays where
      // it was. Like the `×`, this empties the *selection* — the run's points stay on the map, and
      // the control that takes them off says so in its own words.
      const clearEnds = el(
        'button',
        {
          type: 'button',
          id: id('interpolate-clear-ends'),
          title: 'empty both slots — the points already placed stay on the map',
        },
        view.start !== null && view.end !== null ? 'clear both' : 'clear',
      );
      clearEnds.addEventListener('click', () => handlers.onClearEndpoints());
      picked.append(clearEnds);
      section.append(picked);
    }

    section.append(stepControl(view, handlers));

    // **The sampler is stated, never chosen here** — it is the decoder tool's control and this
    // route honours it. Under anything but greedy the dedup describes one draw rather than the
    // model, because two identical latents can decode differently, so it says so.
    if (view.sampler !== 'greedy') {
      section.append(
        el(
          'p',
          { class: 'warn' },
          `sampler is ${view.sampler}: identical latents can decode differently, so “unique” describes this draw rather than the model`,
        ),
      );
    }

    const refusals = [view.start, view.end]
      .filter((e): e is Endpoint => e !== null)
      .map((e) => e.refusal)
      .filter((r): r is string => r !== null);

    if (view.running !== null) {
      section.append(
        el(
          'p',
          { class: 'progress' },
          view.running.loadingProjection
            ? 'loading the projection, then decoding the walk — the first run on a UMAP view reads a large reducer and can take half a minute'
            : 'decoding the walk…',
        ),
      );
      const cancel = el(
        'button',
        { type: 'button', id: id('interpolate-cancel'), title: 'stop waiting for this run' },
        'cancel',
      );
      cancel.addEventListener('click', () => handlers.onCancel());
      section.append(el('p', { class: 'actions' }, cancel));
    } else {
      const run = el(
        'button',
        {
          type: 'button',
          id: id('interpolate-run'),
          title: 'decode the latent line between the two points',
        },
        'interpolate',
      );
      run.disabled = view.start === null || view.end === null || refusals.length > 0;
      run.addEventListener('click', () => handlers.onRun());
      const actions = el('p', { class: 'actions' }, run);
      section.append(actions);
      // **Said before the request, not after** (§5.1). The count is what the reader loses, and it
      // is theirs to accept — the button stays live.
      if (!run.disabled && view.forecast.evicting > 0) {
        actions.append(
          el(
            'span',
            { class: 'warn' },
            // **"up to", because the dedup runs after the decode.** The forecast is computed from
            // the steps *asked for*, which is the worst case; a walk that collapses to four unique
            // sequences places four. Overstating the loss is the safe direction for a notice whose
            // whole job is to let the reader decline.
            ` this view holds ${view.forecast.held} of its ${view.forecast.cap} decoded points, so this run drops up to ${view.forecast.evicting} of the oldest`,
          ),
        );
      }
    }

    const result = view.result;
    if (result !== null) {
      section.append(summary(result));
      if (view.placed.length === 0) {
        const said = nothingPlaced(result, view.includeEndpointMatches);
        if (said !== null) section.append(said);
      }
      const list = el('ol', { class: 'steps', id: id('interpolate-steps-list') });
      view.placed.forEach((placed, at) =>
        list.append(stepEntry(handlers, placed, result, at + 1)),
      );
      section.append(list);

      const include = el('input', { type: 'checkbox', id: id('interpolate-include-echoes') });
      include.checked = view.includeEndpointMatches;
      include.addEventListener('change', () => handlers.onIncludeEndpointMatches(include.checked));
      section.append(
        el(
          'p',
          { class: 'label' },
          el(
            'label',
            { title: 'place the steps whose sequence only reproduces one of the two endpoints' },
            include,
            ' place the endpoints’ own reconstructions too',
          ),
        ),
      );

      if (view.placed.length > 0) {
        const clearRun = el(
          'button',
          {
            type: 'button',
            id: id('interpolate-clear-run'),
            title: 'take this run’s points off the map and forget them',
          },
          `clear these ${view.placed.length} point${view.placed.length === 1 ? '' : 's'}`,
        );
        clearRun.addEventListener('click', () => handlers.onClearRun());
        section.append(el('p', { class: 'actions' }, clearRun));
      }
    }

    root.replaceChildren(section);
  }

  return {
    show,
    clear: () => root.replaceChildren(),
  };
}
